<template>
    <div class="min-h-screen bg-gray-50/50">
        <div class="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
            <!-- Header -->
            <div class="mb-8">
                <div class="flex items-center space-x-3">
                    <div class="flex-shrink-0">
                        <div
                            class="flex h-10 w-10 items-center justify-center rounded-lg bg-orange-500"
                        >
                            <ExclamationTriangleIcon
                                class="h-6 w-6 text-white"
                            />
                        </div>
                    </div>
                    <div>
                        <h1
                            class="text-3xl font-bold tracking-tight text-gray-900"
                        >
                            {{ t("vue.alert_logs") }}
                        </h1>
                        <p class="mt-1 text-lg text-gray-600">
                            Monitor alert notifications and their delivery
                            status across all teams
                        </p>
                    </div>
                </div>
            </div>

            <!-- Main Content Card -->
            <div
                class="overflow-hidden rounded-xl bg-white shadow-sm ring-1 ring-gray-900/5"
            >
                <!-- Card Header -->
                <div class="border-b border-gray-200 bg-white px-6 py-5">
                    <div class="flex items-center justify-between">
                        <div>
                            <h2 class="text-xl font-semibold text-gray-900">
                                Alert History
                            </h2>
                            <p class="mt-1 text-sm text-gray-500">
                                Complete log of all alert notifications with
                                filtering and search capabilities
                            </p>
                        </div>
                        <div class="flex items-center space-x-2">
                            <div class="rounded-lg bg-gray-50 px-3 py-2">
                                <div
                                    class="flex items-center space-x-2 text-sm"
                                >
                                    <div
                                        class="h-2 w-2 rounded-full bg-green-400"
                                    ></div>
                                    <span class="font-medium text-gray-900">{{
                                        alertLogs.total
                                    }}</span>
                                    <span class="text-gray-500"
                                        >total logs</span
                                    >
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Filters Section -->
                <div class="border-b border-gray-200 bg-gray-50/50 px-6 py-5">
                    <div class="space-y-6">
                        <!-- Search Row -->
                        <div class="flex items-center space-x-4">
                            <div class="flex-1">
                                <div class="relative">
                                    <div
                                        class="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3"
                                    >
                                        <MagnifyingGlassIcon
                                            class="h-5 w-5 text-gray-400"
                                        />
                                    </div>
                                    <input
                                        v-model="localFilters.search"
                                        type="text"
                                        :placeholder="
                                            t('vue.search') +
                                            ' teams, domains, reasons...'
                                        "
                                        class="block w-full rounded-lg border-0 py-2.5 pl-10 pr-4 text-gray-900 ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-orange-500 sm:text-sm sm:leading-6"
                                        @input="debouncedApplyFilters"
                                    />
                                </div>
                            </div>
                            <button
                                type="button"
                                class="inline-flex items-center rounded-lg bg-white px-3 py-2.5 text-sm font-medium text-gray-700 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-orange-500"
                                @click="clearFilters"
                            >
                                <XMarkIcon class="mr-2 h-4 w-4" />
                                Clear All
                            </button>
                        </div>

                        <!-- Filter Row -->
                        <div
                            class="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4 xl:grid-cols-6"
                        >
                            <!-- Status Filter -->
                            <div>
                                <label
                                    class="block text-sm font-semibold text-gray-700 mb-2"
                                >
                                    Status
                                </label>
                                <select
                                    v-model="localFilters.status"
                                    class="block w-full rounded-lg border-0 py-2.5 px-3 text-gray-900 ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-orange-500 sm:text-sm"
                                    @change="applyFilters"
                                >
                                    <option value="">All Statuses</option>
                                    <option value="sent">
                                        {{ t("vue.sent") }}
                                    </option>
                                    <option value="failed">
                                        {{ t("vue.failed") }}
                                    </option>
                                    <option value="skipped">
                                        {{ t("vue.skipped") }}
                                    </option>
                                </select>
                            </div>

                            <!-- Channel Filter -->
                            <div>
                                <label
                                    class="block text-sm font-semibold text-gray-700 mb-2"
                                >
                                    Channel
                                </label>
                                <select
                                    v-model="localFilters.channel"
                                    class="block w-full rounded-lg border-0 py-2.5 px-3 text-gray-900 ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-orange-500 sm:text-sm"
                                    @change="applyFilters"
                                >
                                    <option value="">All Channels</option>
                                    <option value="email">
                                        {{ t("vue.email") }}
                                    </option>
                                </select>
                            </div>

                            <!-- Skip Reason Filter -->
                            <div>
                                <label
                                    class="block text-sm font-semibold text-gray-700 mb-2"
                                >
                                    Skip Reason
                                </label>
                                <input
                                    v-model="localFilters.skip_reason"
                                    type="text"
                                    placeholder="Filter by reason..."
                                    class="block w-full rounded-lg border-0 py-2.5 px-3 text-gray-900 ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-orange-500 sm:text-sm"
                                    @input="debouncedApplyFilters"
                                />
                            </div>

                            <!-- Domain Name Filter -->
                            <div>
                                <label
                                    class="block text-sm font-semibold text-gray-700 mb-2"
                                >
                                    Domain
                                </label>
                                <input
                                    v-model="localFilters.domain_name"
                                    type="text"
                                    placeholder="Filter by domain..."
                                    class="block w-full rounded-lg border-0 py-2.5 px-3 text-gray-900 ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-orange-500 sm:text-sm"
                                    @input="debouncedApplyFilters"
                                />
                            </div>

                            <!-- Date From -->
                            <div>
                                <label
                                    class="block text-sm font-semibold text-gray-700 mb-2"
                                >
                                    From Date
                                </label>
                                <input
                                    v-model="localFilters.triggered_from"
                                    type="date"
                                    class="block w-full rounded-lg border-0 py-2.5 px-3 text-gray-900 ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-orange-500 sm:text-sm"
                                    @change="applyFilters"
                                />
                            </div>

                            <!-- Date To -->
                            <div>
                                <label
                                    class="block text-sm font-semibold text-gray-700 mb-2"
                                >
                                    To Date
                                </label>
                                <input
                                    v-model="localFilters.triggered_to"
                                    type="date"
                                    class="block w-full rounded-lg border-0 py-2.5 px-3 text-gray-900 ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-orange-500 sm:text-sm"
                                    @change="applyFilters"
                                />
                            </div>
                        </div>

                        <!-- Items per page selector -->
                        <div class="flex items-center justify-end">
                            <div class="flex items-center space-x-2">
                                <label class="text-sm font-medium text-gray-700"
                                    >Show:</label
                                >
                                <select
                                    v-model="localFilters.per_page"
                                    class="rounded-lg border-0 py-1.5 px-3 text-gray-900 ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-orange-500 sm:text-sm"
                                    @change="applyFilters"
                                >
                                    <option value="10">10</option>
                                    <option value="25">25</option>
                                    <option value="50">50</option>
                                    <option value="100">100</option>
                                </select>
                                <span class="text-sm text-gray-500"
                                    >per page</span
                                >
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Table -->
                <div v-if="alertLogs.data.length > 0" class="px-6 pb-6">
                    <div class="overflow-hidden">
                        <table class="min-w-full">
                            <thead>
                                <tr class="border-b border-gray-200">
                                    <th
                                        scope="col"
                                        class="py-4 pr-3 text-left text-sm font-semibold text-gray-900 cursor-pointer hover:text-orange-600 transition-colors"
                                        @click="updateSort('team_name')"
                                    >
                                        <div
                                            class="flex items-center space-x-1"
                                        >
                                            <UserGroupIcon class="h-4 w-4" />
                                            <span>{{ t("vue.team") }}</span>
                                            <span
                                                v-if="
                                                    filters.sort_by ===
                                                    'team_name'
                                                "
                                                class="text-orange-500"
                                            >
                                                {{
                                                    filters.sort_direction ===
                                                    "asc"
                                                        ? "↑"
                                                        : "↓"
                                                }}
                                            </span>
                                        </div>
                                    </th>
                                    <th
                                        scope="col"
                                        class="px-3 py-4 text-left text-sm font-semibold text-gray-900 cursor-pointer hover:text-orange-600 transition-colors"
                                        @click="updateSort('status')"
                                    >
                                        <div
                                            class="flex items-center space-x-1"
                                        >
                                            <CheckCircleIcon class="h-4 w-4" />
                                            <span>{{ t("vue.status") }}</span>
                                            <span
                                                v-if="
                                                    filters.sort_by === 'status'
                                                "
                                                class="text-orange-500"
                                            >
                                                {{
                                                    filters.sort_direction ===
                                                    "asc"
                                                        ? "↑"
                                                        : "↓"
                                                }}
                                            </span>
                                        </div>
                                    </th>
                                    <th
                                        scope="col"
                                        class="px-3 py-4 text-left text-sm font-semibold text-gray-900 cursor-pointer hover:text-orange-600 transition-colors"
                                        @click="updateSort('channel')"
                                    >
                                        <div
                                            class="flex items-center space-x-1"
                                        >
                                            <EnvelopeIcon class="h-4 w-4" />
                                            <span>{{ t("vue.channel") }}</span>
                                            <span
                                                v-if="
                                                    filters.sort_by ===
                                                    'channel'
                                                "
                                                class="text-orange-500"
                                            >
                                                {{
                                                    filters.sort_direction ===
                                                    "asc"
                                                        ? "↑"
                                                        : "↓"
                                                }}
                                            </span>
                                        </div>
                                    </th>
                                    <th
                                        scope="col"
                                        class="px-3 py-4 text-left text-sm font-semibold text-gray-900 cursor-pointer hover:text-orange-600 transition-colors"
                                        @click="updateSort('compliance_change')"
                                    >
                                        <div
                                            class="flex items-center space-x-1"
                                        >
                                            <span>{{
                                                t("vue.compliance_change")
                                            }}</span>
                                            <span
                                                v-if="
                                                    filters.sort_by ===
                                                    'compliance_change'
                                                "
                                                class="text-orange-500"
                                            >
                                                {{
                                                    filters.sort_direction ===
                                                    "asc"
                                                        ? "↑"
                                                        : "↓"
                                                }}
                                            </span>
                                        </div>
                                    </th>
                                    <th
                                        scope="col"
                                        class="px-3 py-4 text-left text-sm font-semibold text-gray-900 cursor-pointer hover:text-orange-600 transition-colors"
                                        @click="updateSort('domain_name')"
                                    >
                                        <div
                                            class="flex items-center space-x-1"
                                        >
                                            <span>Domain</span>
                                            <span
                                                v-if="
                                                    filters.sort_by ===
                                                    'domain_name'
                                                "
                                                class="text-orange-500"
                                            >
                                                {{
                                                    filters.sort_direction ===
                                                    "asc"
                                                        ? "↑"
                                                        : "↓"
                                                }}
                                            </span>
                                        </div>
                                    </th>
                                    <th
                                        scope="col"
                                        class="px-3 py-4 text-left text-sm font-semibold text-gray-900 cursor-pointer hover:text-orange-600 transition-colors"
                                        @click="updateSort('triggered_at')"
                                    >
                                        <div
                                            class="flex items-center space-x-1"
                                        >
                                            <ClockIcon class="h-4 w-4" />
                                            <span>{{
                                                t("vue.triggered_at")
                                            }}</span>
                                            <span
                                                v-if="
                                                    filters.sort_by ===
                                                    'triggered_at'
                                                "
                                                class="text-orange-500"
                                            >
                                                {{
                                                    filters.sort_direction ===
                                                    "asc"
                                                        ? "↑"
                                                        : "↓"
                                                }}
                                            </span>
                                        </div>
                                    </th>
                                    <th
                                        scope="col"
                                        class="px-3 py-4 text-left text-sm font-semibold text-gray-900"
                                    >
                                        {{ t("vue.skip_reason") }}
                                    </th>
                                </tr>
                            </thead>
                            <tbody class="divide-y divide-gray-100">
                                <tr
                                    v-for="log in alertLogs.data"
                                    :key="log.id"
                                    class="hover:bg-gray-50/60 transition-colors"
                                >
                                    <td class="py-4 pr-3 text-sm">
                                        <div class="flex items-center">
                                            <div class="h-8 w-8 flex-shrink-0">
                                                <div
                                                    class="h-8 w-8 rounded-full bg-gray-100 flex items-center justify-center"
                                                >
                                                    <UserGroupIcon
                                                        class="h-4 w-4 text-gray-600"
                                                    />
                                                </div>
                                            </div>
                                            <div class="ml-3">
                                                <div
                                                    class="font-medium text-gray-900"
                                                >
                                                    {{ log.team_name }}
                                                </div>
                                            </div>
                                        </div>
                                    </td>
                                    <td class="px-3 py-4 text-sm">
                                        <span
                                            class="inline-flex items-center gap-x-1.5 rounded-full px-2.5 py-1 text-xs font-medium"
                                            :class="
                                                getStatusBadgeClass(log.status)
                                            "
                                        >
                                            <component
                                                :is="getStatusIcon(log.status)"
                                                class="h-3 w-3"
                                            />
                                            {{ getStatusLabel(log.status) }}
                                        </span>
                                    </td>
                                    <td class="px-3 py-4 text-sm">
                                        <span
                                            class="inline-flex items-center gap-x-1.5 rounded-full bg-blue-50 px-2.5 py-1 text-xs font-medium text-blue-700"
                                        >
                                            <EnvelopeIcon class="h-3 w-3" />
                                            {{ getChannelLabel(log.channel) }}
                                        </span>
                                    </td>
                                    <td class="px-3 py-4 text-sm">
                                        <span
                                            class="inline-flex items-center rounded-full px-2.5 py-1 text-xs font-medium"
                                            :class="
                                                log.compliance_change > 0
                                                    ? 'bg-red-50 text-red-700'
                                                    : 'bg-green-50 text-green-700'
                                            "
                                        >
                                            {{
                                                log.compliance_change > 0
                                                    ? "-"
                                                    : "+"
                                            }}{{
                                                Math.abs(log.compliance_change)
                                            }}%
                                        </span>
                                    </td>
                                    <td class="px-3 py-4 text-sm">
                                        <span
                                            v-if="log.domain_name"
                                            class="inline-flex items-center rounded-full bg-indigo-50 px-2.5 py-1 text-xs font-medium text-indigo-700"
                                        >
                                            {{ log.domain_name }}
                                        </span>
                                        <span
                                            v-else
                                            class="text-gray-400 italic text-xs"
                                        >
                                            Team-wide
                                        </span>
                                    </td>
                                    <td class="px-3 py-4 text-sm text-gray-600">
                                        <div class="flex items-center">
                                            <ClockIcon
                                                class="mr-1.5 h-3 w-3 text-gray-400"
                                            />
                                            {{ formatDate(log.triggered_at) }}
                                        </div>
                                    </td>
                                    <td
                                        class="px-3 py-4 text-sm text-gray-600 max-w-xs"
                                    >
                                        <span
                                            v-if="log.skip_reason"
                                            class="text-sm"
                                        >
                                            {{ log.skip_reason }}
                                        </span>
                                        <span
                                            v-else
                                            class="text-gray-400 italic"
                                        >
                                            No reason specified
                                        </span>
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>

                <!-- Empty State -->
                <div v-else class="text-center py-16">
                    <div
                        class="mx-auto h-16 w-16 rounded-full bg-gray-100 flex items-center justify-center"
                    >
                        <ExclamationTriangleIcon
                            class="h-8 w-8 text-gray-400"
                        />
                    </div>
                    <h3 class="mt-4 text-lg font-medium text-gray-900">
                        No alert logs found
                    </h3>
                    <p class="mt-2 text-gray-500 max-w-sm mx-auto">
                        No alert logs match your current search criteria. Try
                        adjusting your filters or clearing them to see more
                        results.
                    </p>
                    <div class="mt-6">
                        <button
                            type="button"
                            class="inline-flex items-center rounded-lg bg-orange-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-orange-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-orange-600"
                            @click="clearFilters"
                        >
                            Clear all filters
                        </button>
                    </div>
                </div>

                <!-- Pagination -->
                <div
                    v-if="alertLogs.data.length > 0"
                    class="flex items-center justify-between border-t border-gray-200 bg-white px-6 py-4"
                >
                    <div class="flex flex-1 justify-between sm:hidden">
                        <button
                            :disabled="alertLogs.current_page === 1"
                            class="relative inline-flex items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                            @click="goToPage(alertLogs.current_page - 1)"
                        >
                            Previous
                        </button>
                        <button
                            :disabled="
                                alertLogs.current_page === alertLogs.last_page
                            "
                            class="relative ml-3 inline-flex items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                            @click="goToPage(alertLogs.current_page + 1)"
                        >
                            Next
                        </button>
                    </div>
                    <div
                        class="hidden sm:flex sm:flex-1 sm:items-center sm:justify-between"
                    >
                        <div>
                            <p class="text-sm text-gray-700">
                                Showing
                                <span class="font-medium">{{
                                    alertLogs.from || 0
                                }}</span>
                                to
                                <span class="font-medium">{{
                                    alertLogs.to || 0
                                }}</span>
                                of
                                <span class="font-medium">{{
                                    alertLogs.total
                                }}</span>
                                results
                            </p>
                        </div>
                        <div>
                            <nav
                                class="isolate inline-flex -space-x-px rounded-md shadow-sm"
                                aria-label="Pagination"
                            >
                                <button
                                    :disabled="alertLogs.current_page === 1"
                                    class="relative inline-flex items-center rounded-l-md px-2 py-2 text-gray-400 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 disabled:opacity-50"
                                    @click="
                                        goToPage(alertLogs.current_page - 1)
                                    "
                                >
                                    <ChevronLeftIcon class="h-5 w-5" />
                                </button>
                                <button
                                    v-for="page in visiblePages"
                                    :key="page"
                                    :disabled="page === '...'"
                                    :class="[
                                        page === alertLogs.current_page
                                            ? 'relative z-10 inline-flex items-center bg-indigo-600 px-4 py-2 text-sm font-semibold text-white'
                                            : page === '...'
                                              ? 'relative inline-flex items-center px-4 py-2 text-sm font-semibold text-gray-700'
                                              : 'relative inline-flex items-center px-4 py-2 text-sm font-semibold text-gray-900 ring-1 ring-inset ring-gray-300 hover:bg-gray-50',
                                    ]"
                                    @click="goToPage(page)"
                                >
                                    {{ page }}
                                </button>
                                <button
                                    :disabled="
                                        alertLogs.current_page ===
                                        alertLogs.last_page
                                    "
                                    class="relative inline-flex items-center rounded-r-md px-2 py-2 text-gray-400 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 disabled:opacity-50"
                                    @click="
                                        goToPage(alertLogs.current_page + 1)
                                    "
                                >
                                    <ChevronRightIcon class="h-5 w-5" />
                                </button>
                            </nav>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script lang="ts" setup>
import { computed, ref, watch } from "vue";
import { format, parseISO } from "date-fns";
import { useTranslations } from "@/composables/useTranslations";
import {
    ExclamationTriangleIcon,
    ChevronLeftIcon,
    ChevronRightIcon,
    MagnifyingGlassIcon,
    XMarkIcon,
    UserGroupIcon,
    CheckCircleIcon,
    XCircleIcon,
    ClockIcon,
    EnvelopeIcon,
} from "@heroicons/vue/24/outline";
import { router } from "@inertiajs/vue3";
import { debounce } from "lodash";
import route from "ziggy-js";

interface IAlertLog {
    id: number;
    alert_settings_id: number;
    team_id: number;
    team_name: string;
    compliance_change: number;
    compliance_drop_threshold: number;
    triggered_at: string;
    channel: string;
    status: string;
    skip_reason?: string;
    email_sent: boolean;
    email_error?: string;
    domain_name?: string;
}

interface Props {
    alertLogs: {
        data: IAlertLog[];
        current_page: number;
        last_page: number;
        per_page: number;
        total: number;
        from: number;
        to: number;
    };
    filters: {
        search?: string;
        status?: string;
        channel?: string;
        skip_reason?: string;
        domain_name?: string;
        triggered_from?: string;
        triggered_to?: string;
        per_page: number;
        sort_by: string;
        sort_direction: string;
    };
}

const props = defineProps<Props>();
const { t } = useTranslations();

// Local filters for reactive updates
const localFilters = ref({
    search: props.filters.search || "",
    status: props.filters.status || "",
    channel: props.filters.channel || "",
    skip_reason: props.filters.skip_reason || "",
    domain_name: props.filters.domain_name || "",
    triggered_from: props.filters.triggered_from || "",
    triggered_to: props.filters.triggered_to || "",
    per_page: props.filters.per_page || 25,
});

// Computed properties
const visiblePages = computed(() => {
    const current = props.alertLogs.current_page;
    const last = props.alertLogs.last_page;
    const delta = 2;
    const range = [];
    const rangeWithDots = [];

    for (
        let i = Math.max(2, current - delta);
        i <= Math.min(last - 1, current + delta);
        i++
    ) {
        range.push(i);
    }

    if (current - delta > 2) {
        rangeWithDots.push(1, "...");
    } else {
        rangeWithDots.push(1);
    }

    rangeWithDots.push(...range);

    if (current + delta < last - 1) {
        rangeWithDots.push("...", last);
    } else if (last > 1) {
        rangeWithDots.push(last);
    }

    return rangeWithDots.filter(
        (item, index, array) => array.indexOf(item) === index,
    );
});

// Methods
const applyFilters = () => {
    const queryParams = new URLSearchParams();

    if (localFilters.value.search) {
        queryParams.set("search", localFilters.value.search);
    }
    if (localFilters.value.status) {
        queryParams.set("status", localFilters.value.status);
    }
    if (localFilters.value.channel) {
        queryParams.set("channel", localFilters.value.channel);
    }
    if (localFilters.value.skip_reason) {
        queryParams.set("skip_reason", localFilters.value.skip_reason);
    }
    if (localFilters.value.domain_name) {
        queryParams.set("domain_name", localFilters.value.domain_name);
    }
    if (localFilters.value.triggered_from) {
        queryParams.set("triggered_from", localFilters.value.triggered_from);
    }
    if (localFilters.value.triggered_to) {
        queryParams.set("triggered_to", localFilters.value.triggered_to);
    }
    if (localFilters.value.per_page !== 25) {
        queryParams.set("per_page", localFilters.value.per_page.toString());
    }

    // Keep current sorting
    queryParams.set("sort_by", props.filters.sort_by);
    queryParams.set("sort_direction", props.filters.sort_direction);

    // Reset to first page when filtering
    queryParams.set("page", "1");

    router.get(
        route("kiosk.alert-logs.index"),
        Object.fromEntries(queryParams),
        {
            preserveState: true,
            preserveScroll: true,
        },
    );
};

const debouncedApplyFilters = debounce(applyFilters, 500);

const clearFilters = () => {
    localFilters.value = {
        search: "",
        status: "",
        channel: "",
        skip_reason: "",
        domain_name: "",
        triggered_from: "",
        triggered_to: "",
        per_page: 25,
    };

    router.get(
        route("kiosk.alert-logs.index"),
        {},
        {
            preserveState: true,
            preserveScroll: true,
        },
    );
};

const goToPage = (page: number | string) => {
    if (
        typeof page !== "number" ||
        page < 1 ||
        page > props.alertLogs.last_page
    ) {
        return;
    }

    const queryParams = new URLSearchParams(window.location.search);
    queryParams.set("page", page.toString());

    router.get(
        route("kiosk.alert-logs.index"),
        Object.fromEntries(queryParams),
        {
            preserveState: true,
            preserveScroll: true,
        },
    );
};

const updateSort = (column: string) => {
    const queryParams = new URLSearchParams(window.location.search);

    if (props.filters.sort_by === column) {
        // Toggle direction
        const newDirection =
            props.filters.sort_direction === "asc" ? "desc" : "asc";
        queryParams.set("sort_direction", newDirection);
    } else {
        // New column, default to desc
        queryParams.set("sort_by", column);
        queryParams.set("sort_direction", "desc");
    }

    router.get(
        route("kiosk.alert-logs.index"),
        Object.fromEntries(queryParams),
        {
            preserveState: true,
            preserveScroll: true,
        },
    );
};

// Helper methods
const getStatusBadgeClass = (status: string): string => {
    switch (status) {
        case "sent":
            return "bg-green-50 text-green-700";
        case "failed":
            return "bg-red-50 text-red-700";
        case "skipped":
            return "bg-yellow-50 text-yellow-700";
        default:
            return "bg-gray-50 text-gray-700";
    }
};

const getStatusIcon = (status: string) => {
    switch (status) {
        case "sent":
            return CheckCircleIcon;
        case "failed":
            return XCircleIcon;
        case "skipped":
            return ClockIcon;
        default:
            return ClockIcon;
    }
};

const getStatusLabel = (status: string): string => {
    switch (status) {
        case "sent":
            return t("vue.sent");
        case "failed":
            return t("vue.failed");
        case "skipped":
            return t("vue.skipped");
        default:
            return status;
    }
};

const getChannelLabel = (channel: string): string => {
    switch (channel) {
        case "email":
            return t("vue.email");
        default:
            return channel;
    }
};

const formatDate = (dateString: string): string => {
    try {
        return format(parseISO(dateString), "MMM dd, yyyy HH:mm");
    } catch (error) {
        return dateString;
    }
};

// Watch for prop changes to update local filters
watch(
    () => props.filters,
    (newFilters) => {
        localFilters.value = {
            search: newFilters.search || "",
            status: newFilters.status || "",
            channel: newFilters.channel || "",
            skip_reason: newFilters.skip_reason || "",
            domain_name: newFilters.domain_name || "",
            triggered_from: newFilters.triggered_from || "",
            triggered_to: newFilters.triggered_to || "",
            per_page: newFilters.per_page || 25,
        };
    },
    { immediate: true },
);
</script>
